import { useMemo, useState } from "react";
import { FlaskConical, Trophy } from "lucide-react";
import { Card, Button } from "@/ui";
import { cn } from "@/lib/cn";
import { useWorkspace } from "@/app/WorkspaceContext";
import {
  usePages,
  useExperiments,
  useProjectExperiments,
  useExperimentStats,
  useSetExperimentStatus,
} from "@/data/hooks";
import type { Experiment, ExperimentStats, ExperimentStatus } from "@/data/services";
import { useAnalyticsPageScope } from "@/data/analyticsScope";
import { StatusPill, SkeletonLine } from "@/screens/you/parts";
import { PanelHeader } from "../parts";
import { matches, useSignalsQuery } from "../searchContext";
import { AigenTag, SampleChip } from "../wiring";

/* ------------------------------------------------------------------ status */

const STATUS_TONE: Record<ExperimentStatus, "ok" | "pending" | "muted"> = {
  draft: "muted",
  running: "ok",
  paused: "pending",
  concluded: "muted",
};
const STATUS_LABEL: Record<ExperimentStatus, string> = {
  draft: "Draft",
  running: "Running",
  paused: "Paused",
  concluded: "Concluded",
};

/* ----------------------------------------------------------------- samples */
// Honest fallback: when the workspace is on mock data, there's no project, or
// the experiments endpoint errors, we show this bundled set marked `sample`.
// These never touch a backend (their stats are inlined, lifecycle is disabled).

const SAMPLE_EXPERIMENTS: Experiment[] = [
  {
    id: "sample-exp-hero",
    page_id: "/",
    name: "Hero headline urgency",
    hypothesis: "A time-bound promise (“in 30 seconds”) lifts sign-ups vs. the generic headline.",
    scope: { node_id: "hero" },
    status: "running",
    allocation: { traffic_pct: 100 },
    variants: [
      { id: "c_hero", is_control: true, weight: 50, label: "Control" },
      { id: "v_hero", is_control: false, weight: 50, label: "Urgency" },
    ],
    goals: [{ event_name: "signup_complete", primary: true }],
  },
  {
    id: "sample-exp-pricing",
    page_id: "/pricing",
    name: "Pricing CTA color",
    hypothesis: "A coral primary CTA out-converts the quiet ink button.",
    scope: { node_id: "pricing-cta" },
    status: "concluded",
    allocation: { traffic_pct: 60 },
    variants: [
      { id: "c_price", is_control: true, weight: 50, label: "Ink button" },
      { id: "v_price", is_control: false, weight: 50, label: "Coral button" },
    ],
    goals: [{ event_name: "checkout_start", primary: true }],
    winner_variant_id: "v_price",
  },
];

const SAMPLE_STATS: Record<string, ExperimentStats> = {
  "sample-exp-hero": {
    experiment_id: "sample-exp-hero",
    variants: [
      { variant_id: "c_hero", is_control: true, exposures: 4820, conversions: 109, conversion_rate: 0.0226, credible_interval_low: 0.0187, credible_interval_high: 0.0272, prob_beat_control: null },
      { variant_id: "v_hero", is_control: false, exposures: 4774, conversions: 152, conversion_rate: 0.0318, credible_interval_low: 0.0272, credible_interval_high: 0.0371, prob_beat_control: 0.971 },
    ],
  },
  "sample-exp-pricing": {
    experiment_id: "sample-exp-pricing",
    variants: [
      { variant_id: "c_price", is_control: true, exposures: 8120, conversions: 405, conversion_rate: 0.0499, credible_interval_low: 0.0453, credible_interval_high: 0.0548, prob_beat_control: null },
      { variant_id: "v_price", is_control: false, exposures: 8090, conversions: 511, conversion_rate: 0.0632, credible_interval_low: 0.0582, credible_interval_high: 0.0685, prob_beat_control: 0.998 },
    ],
  },
};

/* -------------------------------------------------------------------- panel */

export function Experiments() {
  const { currentProjectId, live } = useWorkspace();
  const pageId = useAnalyticsPageScope();
  const q = useSignalsQuery();

  // Page-scoped when a page filter is active; otherwise aggregate across the
  // whole project. Both hooks are always called; the `enabled` flag (driven by
  // the id we pass) gates which one actually fetches.
  const pageQ = useExperiments(pageId ?? undefined);
  const projectQ = useProjectExperiments(pageId ? undefined : currentProjectId);

  const items: Experiment[] = pageId ? pageQ.data?.items ?? [] : projectQ.data ?? [];
  const isLoading = pageId ? pageQ.isLoading : projectQ.isLoading;
  const isError = pageId ? pageQ.isError : projectQ.isError;

  // Resolve page_id → slug for a friendly "which page" label.
  const pagesQ = usePages(currentProjectId);
  const pageSlug = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pagesQ.data ?? []) m.set(p.id, p.slug);
    return m;
  }, [pagesQ.data]);
  const pageLabel = (id: string) => pageSlug.get(id) ?? id;

  const useSample = !live || !currentProjectId || isError;
  const list = (useSample ? SAMPLE_EXPERIMENTS : items).filter((e) => matches(q, e.name, e.hypothesis));

  return (
    <div className="stagger space-y-6">
      <PanelHeader
        title="A/B tests"
        desc="Split tests with Bayesian lift — start, pause, and call winners in one place."
        action={useSample ? <SampleChip /> : <AigenTag />}
      />

      {!useSample && isLoading ? (
        <div className="space-y-4">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : list.length === 0 ? (
        q ? (
          <EmptyBlock title={`No A/B tests match “${q}”`} detail="Try a different name or hypothesis." />
        ) : (
          <EmptyBlock
            title="No A/B tests yet"
            detail="Launch one from Make to start splitting traffic and measuring lift."
          />
        )
      ) : (
        <div className="space-y-4">
          {useSample
            ? list.map((e) => (
                <ExperimentCardView
                  key={e.id}
                  exp={e}
                  stats={SAMPLE_STATS[e.id]}
                  statsLoading={false}
                  pageLabel={pageLabel(e.page_id)}
                  disabled
                />
              ))
            : list.map((e) => <LiveExperimentCard key={e.id} exp={e} pageLabel={pageLabel(e.page_id)} />)}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------- live card (wired) */

function LiveExperimentCard({ exp, pageLabel }: { exp: Experiment; pageLabel: string }) {
  const statsQ = useExperimentStats(exp.id);
  const setStatus = useSetExperimentStatus();
  const err = setStatus.error instanceof Error ? setStatus.error.message : setStatus.error ? "Failed" : null;
  return (
    <ExperimentCardView
      exp={exp}
      stats={statsQ.data}
      statsLoading={statsQ.isLoading}
      pageLabel={pageLabel}
      busy={setStatus.isPending}
      error={err}
      onSetStatus={(status, winnerVariantId) => setStatus.mutate({ id: exp.id, status, winnerVariantId })}
    />
  );
}

/* --------------------------------------------------- card view (presentation) */

function ExperimentCardView({
  exp,
  stats,
  statsLoading,
  pageLabel,
  busy,
  disabled,
  error,
  onSetStatus,
}: {
  exp: Experiment;
  stats: ExperimentStats | undefined;
  statsLoading: boolean;
  pageLabel: string;
  busy?: boolean;
  disabled?: boolean;
  error?: string | null;
  onSetStatus?: (status: ExperimentStatus, winnerVariantId?: string) => void;
}) {
  const primaryGoal = exp.goals?.find((g) => g.primary) ?? exp.goals?.[0];
  const set = (status: ExperimentStatus, winner?: string) => {
    if (disabled || busy) return;
    onSetStatus?.(status, winner);
  };

  return (
    <Card className="p-5">
      {/* header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-[15px] font-semibold tracking-tight text-ink-strong">{exp.name}</h3>
          {exp.hypothesis && (
            <p className="mt-1 max-w-prose text-[13px] italic leading-relaxed text-muted">{exp.hypothesis}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-subtle">
            <span className="font-mono text-ink">{pageLabel}</span>
            <span aria-hidden>·</span>
            <span>
              <span className="tabular-nums text-muted">{exp.allocation.traffic_pct}%</span> traffic
            </span>
            {primaryGoal && (
              <>
                <span aria-hidden>·</span>
                <span>
                  goal <span className="font-mono text-muted">{primaryGoal.event_name}</span>
                </span>
              </>
            )}
          </div>
        </div>
        <StatusPill tone={STATUS_TONE[exp.status]}>{STATUS_LABEL[exp.status]}</StatusPill>
      </div>

      {/* stats table */}
      <div className="mt-4">
        {statsLoading ? (
          <div className="space-y-2 py-1">
            <SkeletonLine className="h-3 w-full" />
            <SkeletonLine className="h-3 w-full" />
          </div>
        ) : (
          <StatsTable exp={exp} stats={stats} />
        )}
      </div>

      {/* lifecycle */}
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-hairline pt-3">
        {error && <span className="mr-auto text-[12px] text-accent">{error}</span>}
        {exp.status === "concluded" ? (
          <ConcludedReadout exp={exp} />
        ) : (
          <>
            {exp.status === "draft" && (
              <Button variant="coral" size="sm" disabled={disabled || busy} onClick={() => set("running")}>
                Start
              </Button>
            )}
            {exp.status === "paused" && (
              <Button variant="coral" size="sm" disabled={disabled || busy} onClick={() => set("running")}>
                Resume
              </Button>
            )}
            {exp.status === "running" && (
              <Button variant="ghost" size="sm" disabled={disabled || busy} onClick={() => set("paused")}>
                Pause
              </Button>
            )}
            {(exp.status === "running" || exp.status === "paused") && (
              <ConcludeControl
                exp={exp}
                stats={stats}
                disabled={disabled || busy}
                onConclude={(winner) => set("concluded", winner)}
              />
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- stats table */

function StatsTable({ exp, stats }: { exp: Experiment; stats: ExperimentStats | undefined }) {
  const byVariant = new Map(stats?.variants.map((s) => [s.variant_id, s]) ?? []);

  // The leading non-control variant (highest prob_beat_control). It earns a
  // "winning" chip only once it crosses the 95% confidence threshold.
  let leaderId: string | null = null;
  let leaderProb = -1;
  for (const s of stats?.variants ?? []) {
    if (s.is_control || s.prob_beat_control == null) continue;
    if (s.prob_beat_control > leaderProb) {
      leaderProb = s.prob_beat_control;
      leaderId = s.variant_id;
    }
  }
  const winningId = leaderProb >= 0.95 ? leaderId : null;

  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr className="text-subtle">
          <th className="px-1 pb-2 text-left font-mono text-[10px] uppercase tracking-wide">Variant</th>
          <th className="px-1 pb-2 text-right font-mono text-[10px] uppercase tracking-wide">Exposures</th>
          <th className="px-1 pb-2 text-right font-mono text-[10px] uppercase tracking-wide">Conversions</th>
          <th className="px-1 pb-2 text-right font-mono text-[10px] uppercase tracking-wide">Conv. rate</th>
          <th className="px-1 pb-2 text-right font-mono text-[10px] uppercase tracking-wide">P(beat ctrl)</th>
        </tr>
      </thead>
      <tbody>
        {exp.variants.map((v) => {
          const s = byVariant.get(v.id);
          const isWinner = exp.winner_variant_id === v.id;
          const isWinning = winningId === v.id;
          return (
            <tr key={v.id} className="border-t border-hairline">
              <td className="px-1 py-2">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className={cn("text-ink", (isWinner || isWinning) && "font-semibold text-ink-strong")}>
                    {v.label}
                  </span>
                  {v.is_control && (
                    <span className="rounded-chip bg-paper-2 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-subtle dark:bg-white/5">
                      control
                    </span>
                  )}
                  {isWinner && (
                    <span className="inline-flex items-center gap-1 rounded-chip bg-accent-soft px-1.5 py-0.5 text-[10px] font-medium text-accent">
                      <Trophy className="h-3 w-3" strokeWidth={2} /> winner
                    </span>
                  )}
                  {!isWinner && isWinning && (
                    <span className="rounded-chip bg-accent-soft px-1.5 py-0.5 text-[10px] font-medium text-accent">
                      winning
                    </span>
                  )}
                </span>
              </td>
              <td className="px-1 py-2 text-right tabular-nums text-ink">
                {s ? s.exposures.toLocaleString() : "—"}
              </td>
              <td className="px-1 py-2 text-right tabular-nums text-ink">
                {s ? s.conversions.toLocaleString() : "—"}
              </td>
              <td className="px-1 py-2 text-right tabular-nums text-ink">
                {s ? `${(s.conversion_rate * 100).toFixed(2)}%` : "—"}
              </td>
              <td className="px-1 py-2 text-right tabular-nums">
                {s == null || s.prob_beat_control == null ? (
                  <span className="text-subtle">—</span>
                ) : (
                  <span className={cn(s.prob_beat_control >= 0.95 ? "font-medium text-accent" : "text-muted")}>
                    {(s.prob_beat_control * 100).toFixed(1)}%
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* ------------------------------------------------------------ conclude control */

function ConcludeControl({
  exp,
  stats,
  disabled,
  onConclude,
}: {
  exp: Experiment;
  stats: ExperimentStats | undefined;
  disabled?: boolean;
  onConclude: (winnerVariantId?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [winner, setWinner] = useState(() => defaultWinner(exp, stats));

  return (
    <div className="relative">
      <Button variant="ghost" size="sm" disabled={disabled} onClick={() => setOpen((v) => !v)}>
        Conclude…
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-40 mt-2 w-60 rounded-card border border-hairline bg-paper p-3 shadow-lg dark:bg-ink-2">
            <label className="eyebrow mb-1.5 block">Pick the winner</label>
            <select
              value={winner}
              onChange={(e) => setWinner(e.target.value)}
              className="w-full rounded-btn border border-hairline bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none focus:border-accent/50 dark:bg-white/5"
            >
              {exp.variants.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                  {v.is_control ? " (control)" : ""}
                </option>
              ))}
            </select>
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="quiet" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="coral"
                size="sm"
                onClick={() => {
                  setOpen(false);
                  onConclude(winner || undefined);
                }}
              >
                Conclude
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function defaultWinner(exp: Experiment, stats: ExperimentStats | undefined): string {
  let bestId = "";
  let bestProb = -1;
  for (const s of stats?.variants ?? []) {
    if (s.is_control || s.prob_beat_control == null) continue;
    if (s.prob_beat_control > bestProb) {
      bestProb = s.prob_beat_control;
      bestId = s.variant_id;
    }
  }
  if (bestId) return bestId;
  return exp.variants.find((v) => !v.is_control)?.id ?? exp.variants[0]?.id ?? "";
}

/* --------------------------------------------------------------- concluded read */

function ConcludedReadout({ exp }: { exp: Experiment }) {
  const winner = exp.variants.find((v) => v.id === exp.winner_variant_id);
  if (!winner) return <span className="text-[12px] text-subtle">Concluded — no winner recorded</span>;
  return (
    <span className="inline-flex items-center gap-2 text-[13px] text-muted">
      <span>Winner</span>
      <span className="inline-flex items-center gap-1 rounded-chip bg-accent-soft px-2 py-0.5 text-[12px] font-medium text-accent">
        <Trophy className="h-3.5 w-3.5" strokeWidth={2} />
        {winner.label}
      </span>
    </span>
  );
}

/* ----------------------------------------------------------------- empty/skel */

function EmptyBlock({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="grid place-items-center rounded-card border border-dashed border-hairline px-6 py-14 text-center">
      <div className="max-w-xs">
        <span className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-paper-2 text-subtle dark:bg-white/5">
          <FlaskConical className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <p className="text-[14px] font-medium text-ink-strong">{title}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-muted">{detail}</p>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <SkeletonLine className="h-4 w-48" />
          <SkeletonLine className="h-3 w-72" />
        </div>
        <SkeletonLine className="h-6 w-20 rounded-chip" />
      </div>
      <div className="mt-5 space-y-2.5">
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonLine key={i} className="h-3 w-full" />
        ))}
      </div>
    </Card>
  );
}
